// Cloudflare Turnstile widget for the public survey. Renders only when a site
// key is configured (server-driven). The script is loaded once, on demand, and
// the widget is rendered explicitly so it fits the SPA lifecycle. A spent or
// expired token clears itself; the parent remounts (via key) to get a fresh one.
//
// SAYING WHAT HAPPENED (cca-2s7tg, after the forms engine's cca-sphc in
// cca-forms-web/src/components/Turnstile.tsx). The submit door refuses a
// submission without a token (ADR-072 decision 1), so a widget that cannot give
// one must say so: otherwise Submit stays disabled, nothing reaches the server,
// and no counter sees it. `onFailure(kind)` reports the three ways it stalls:
//   - 'load'      the script never loaded (a content blocker, a filtered
//                 church office network);
//   - 'challenge' the challenge errored, the browser is unsupported, or the
//                 loaded script would not draw the widget (a bad site key);
//   - 'slow'      no token, no challenge on screen and no error after
//                 TURNSTILE_WAIT_MS.
// `onToken('')` means "no token right now, and nothing known to be wrong":
// still checking, waiting for the person to answer a challenge, refreshing an
// expired token, or gone. A widget that UNMOUNTS takes its token with it,
// because a removed widget never fires `expired` and its token would otherwise
// sit in the page past its five minutes, to be refused at submit.
//
// A failed load is FORGOTTEN, not cached, so a remount (the page's "Try the
// check again") loads the script afresh, without asking anyone to reload.

import { useEffect, useRef, type JSX } from 'react';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      'timeout-callback'?: () => void;
      'before-interactive-callback'?: () => void;
      'unsupported-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      appearance?: 'always' | 'execute' | 'interaction-only';
      size?: 'normal' | 'flexible' | 'compact';
    },
  ) => string;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Why the widget has no token to give and will not get one by waiting: its
 *  script did not load, its challenge failed (or the browser is unsupported),
 *  or no token has come after TURNSTILE_WAIT_MS. */
export type TurnstileFailure = 'load' | 'challenge' | 'slow';

/** How long the widget waits for a token before reporting 'slow'. Generous:
 *  the non-interactive check can take several seconds on a slow phone. The
 *  forms engine waits the same (cca-sphc). */
export const TURNSTILE_WAIT_MS = 20_000;

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      // Forget the failure so a later mount tries again, and take the dead
      // tag with it rather than leaving one per attempt in <head>.
      scriptPromise = null;
      s.remove();
      reject(new Error('turnstile_script_failed'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onFailure,
}: {
  siteKey: string;
  /** The widget's token, or '' when it has none right now and nothing is known
   *  to be wrong (see the header). */
  onToken: (token: string) => void;
  /** The check cannot give a token by waiting: the page says so and offers a
   *  retry (a remount). */
  onFailure: (kind: TurnstileFailure) => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Keep the latest callbacks without re-running the render effect.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;

  useEffect(() => {
    let cancelled = false;
    // The wait for a token: armed on mount and after an expiry; stopped by a
    // token, by a challenge the person is now answering, or by a failure.
    let waitTimer: number | null = null;
    const stopWaiting = (): void => {
      if (waitTimer !== null) window.clearTimeout(waitTimer);
      waitTimer = null;
    };
    const startWaiting = (): void => {
      stopWaiting();
      waitTimer = window.setTimeout(() => {
        waitTimer = null;
        if (!cancelled) onFailureRef.current('slow');
      }, TURNSTILE_WAIT_MS);
    };
    const fail = (kind: TurnstileFailure): void => {
      stopWaiting();
      if (!cancelled) onFailureRef.current(kind);
    };

    startWaiting();
    loadTurnstile().then(
      () => {
        if (cancelled || !ref.current || !window.turnstile || widgetIdRef.current) return;
        try {
          widgetIdRef.current = window.turnstile.render(ref.current, {
            sitekey: siteKey,
            theme: 'light', // match the survey's light paper look
            // Stay invisible and pass silently for normal visitors; only show the
            // (unstyleable) Cloudflare box if a real challenge is actually needed.
            appearance: 'interaction-only',
            callback: (t) => {
              stopWaiting();
              onTokenRef.current(t);
            },
            // Turnstile refreshes an expired token itself: drop the old one and
            // wait for the new one.
            'expired-callback': () => {
              onTokenRef.current('');
              startWaiting();
            },
            // A challenge was on screen and not answered in time; the widget
            // offers it again, so this is no failure.
            'timeout-callback': () => onTokenRef.current(''),
            // A real challenge is now on screen: the person is answering it,
            // not waiting on us, so the wait stops (and a 'slow' already said
            // is withdrawn).
            'before-interactive-callback': () => {
              stopWaiting();
              onTokenRef.current('');
            },
            'error-callback': () => {
              onTokenRef.current('');
              fail('challenge');
            },
            'unsupported-callback': () => fail('challenge'),
          });
        } catch {
          // The script loaded but would not draw the widget (a bad site key,
          // say): the check cannot run, which is the challenge failing, not a
          // blocked load.
          fail('challenge');
        }
      },
      // The script did not load: say so, rather than leave the page waiting
      // for a token that cannot come.
      () => fail('load'),
    );
    return () => {
      cancelled = true;
      stopWaiting();
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
      // A widget that is gone can neither refresh nor expire its token, so the
      // page must not keep it (see the header).
      onTokenRef.current('');
    };
  }, [siteKey]);

  return <div ref={ref} style={{ margin: '4px 0 12px' }} />;
}
